<script setup lang="ts">
import SidebarFooter from "@/components/layout/SidebarFooter.vue";
import {
  useSidebarNavigation,
  type NavItem,
} from "@/composables/useSidebarNavigation";

const {
  navItems,
  isAdminExpanded,
  isAdminRoute,
  isActive,
  toggleAdmin,
  navigateTo,
  filterMenuItems,
} = useSidebarNavigation();
</script>

<template>
  <aside
    class="w-64 bg-white border-r border-gray-200 min-h-screen flex flex-col"
  >
    <nav class="flex-1 py-6 px-4 space-y-1 overflow-y-auto scroll-smooth-none">
      <template v-for="item in navItems" :key="item.path">
        <!-- Admin Menu with Children -->
        <div v-if="item.children">
          <!-- Admin Parent Button -->
          <button
            type="button"
            tabindex="-1"
            :class="[
              'w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
              isAdminRoute
                ? 'bg-primary text-white'
                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
            ]"
            @click.prevent="toggleAdmin"
          >
            <div class="flex items-center gap-3">
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  :d="item.icon"
                />
              </svg>
              <span>{{ item.name }}</span>
            </div>
            <!-- Chevron Icon -->
            <svg
              class="w-4 h-4 transition-transform"
              :class="{ 'rotate-180': isAdminExpanded || isAdminRoute }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          <!-- Admin Submenu -->
          <div
            :class="[
              'ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-2 transition-all duration-200',
              isAdminExpanded || isAdminRoute
                ? 'max-h-screen opacity-100'
                : 'max-h-0 opacity-0 overflow-hidden',
            ]"
          >
            <template
              v-for="(child, index) in filterMenuItems(item.children)"
              :key="index"
            >
              <!-- Category Divider -->
              <div
                v-if="'type' in child && child.type === 'divider'"
                class="pt-3 pb-1 px-3"
              >
                <h3
                  class="text-xs font-semibold text-gray-500 uppercase tracking-wider"
                >
                  {{ child.label }}
                </h3>
              </div>

              <!-- Menu Item -->
              <div
                v-else
                role="button"
                tabindex="0"
                :class="[
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer',
                  isActive((child as NavItem).path)
                    ? 'bg-primary/10 text-primary-dark font-semibold'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                  (child as NavItem).deprecated ? 'opacity-50' : '',
                ]"
                @click="(e) => navigateTo((child as NavItem).path, e)"
                @keydown.enter="(e) => navigateTo((child as NavItem).path, e)"
                @keydown.space.prevent="
                  (e) => navigateTo((child as NavItem).path, e)
                "
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    :d="(child as NavItem).icon"
                  />
                </svg>
                <span>{{ (child as NavItem).name }}</span>
              </div>
            </template>
          </div>
        </div>

        <!-- Regular Menu Items -->
        <div
          v-else
          role="button"
          tabindex="0"
          :class="[
            'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer',
            isActive(item.path)
              ? 'bg-primary text-white'
              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
          ]"
          @click="(e) => navigateTo(item.path, e)"
          @keydown.enter="(e) => navigateTo(item.path, e)"
          @keydown.space.prevent="(e) => navigateTo(item.path, e)"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              :d="item.icon"
            />
          </svg>
          <span>{{ item.name }}</span>
        </div>
      </template>
    </nav>

    <!-- Version Info & WebSocket Status -->
    <SidebarFooter />
  </aside>
</template>
